import { IhcNameValidator } from '@shared/validators/ihc-name-validator';
import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { SharedService } from '@shared/services/shared.service';
import { IInHomeCareLicense } from 'app/models/licenses/in-home-care/in-home-care-license';
import { InHomeCareLicenseService } from 'app/services/license/in-home-care/in-home-care-license.service';
import { Subscription, filter } from 'rxjs';
import { AdministratorApplicationComponent } from './administrator-application/administrator-application.component';
import { DocumentComponent } from '../../reusable/document/document.component';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { EntityOwnerComponent } from '../../reusable/entity-owner/entity-owner.component';
import { AddressComponent } from '../../reusable/address/address.component';
import { IDocument } from 'app/models/documents/document';
import { IhcParentValidator } from '@shared/validators/ihc-parent-validator';
import { IAddress } from 'app/models/address/address';
import { OfficeHoursComponent } from '../../reusable/office-hours/office-hours.component';
import { BranchOperationComponent } from '../../reusable/branch-operation/branch-operation.component';
import { ILicenseChangeDialog } from './in-home-care-change/in-home-care-change-dialog.component';
import { ExportInHomeCareLicenseDialogComponent } from './dialog-export-in-home-care-license/dialog-export-in-home-care-license.component';
import { SubunitDetailsDialogComponent } from './dialog-subunit-details/dialog-subunit-details.component';
import { IStatus } from 'app/models/licenses/status';
import { ILicenseRequest } from 'app/models/licenses/license-request';
import { AuthService } from '@shared/auth/auth.service';
import { IhcLicenseValidator } from '@shared/validators/ihc-license-validator';
import { PaymentService } from 'app/components/payments/service/payment.service';
import { IhcParentNullableValidator } from '@shared/validators/ihc-parent-nullable-validator';
import { InHomeCareChangeService } from 'app/services/license/in-home-care/in-home-care-change.service';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  providers: [IhcNameValidator],
  selector: 'app-in-home-care',
  templateUrl: './in-home-care.component.html',
  styleUrls: ['./in-home-care.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class InHomeCareComponent implements OnInit, OnDestroy {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChild(AddressComponent) addresses!: AddressComponent;
  @ViewChild(OfficeHoursComponent) officeHours!: OfficeHoursComponent;
  @ViewChild(EntityOwnerComponent) owners!: EntityOwnerComponent;
  @ViewChild(BranchOperationComponent) branchOperations!: BranchOperationComponent;
  @ViewChild(AdministratorApplicationComponent) adminApplication!: AdministratorApplicationComponent;

  public routeSubscription?: Subscription;
  public showClaimLicense: boolean = false;
  public deleteId: number = 0;
  public classification: string = '';
  public isParent: boolean = true;
  public addressesLoaded: boolean = false;
  public distanceInvalid: boolean = false;
  public managingChanges: boolean = false;
  public changeLicenseId: number = 0;
  public changeLicenseGuid: string = '';
  public inHomeCareTypes: string[] = ['Proof of Business Zoning Approval or Allowance'];
  public policyAndProcedureTypes: string[] = ['IHC Policies, Procedures and Forms', 'Survey Documents', 'Policy and Procedure Checklist', 'Other'];

  public validationErrors: string[] = [];
  public branchErrors: string[] = [];
  public ownerErrors: string[] = [];
  public adminErrors: string[] = [];
  public documentErrors: string[] = [];

  public parentId: number = 0;
  public parentGuid: string = '';
  public parentName: string = '';

  public license: IInHomeCareLicense = {
    id: 0,
    userId: '',
    licenseId: '',
    enteredByStaff: false,
    assignedToId: '',
    assignedToName: '',
    finalApprovalBy: '',
    canRenew: false,
    isRenewal: false,
    electronicSignature: '',
    signedDate: '',
    applicationDate: '',
    approvedDate: '',
    deniedDate: '',
    expirationDate: '',
    entityName: '',
    entityWebsite: '',
    businessInOregon: false,
    secretaryOfStateNumber: '',
    registeredBusiness: false,
    registeredIn: '',
    registryNumber: '',
    isSubunit: false,
    parentAgency: '',
    parentGuid: '',
    parentCity: '',
    calculatedDistance: 0,
    agencyDBAName: '',
    agencyPhone: '',
    agencyEmail: '',
    agencyFax: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    agencyPhysicalLocation: '',
    otherLocation: '',
    classificationLevel: '',
    adminDesigneeName: '',
    adminDesigneeTitle: '',
    qualifiedEntityName: '',
    qualifiedEntityTitle: '',
    registeredNurseName: '',
    adminPrintName: '',
    adminPrintTitle: '',
    backgroundCheckDate: '',
    initialLicensure: '',
    staffInitials: '',
    statuses: []
  }

  public nameValidator = inject(IhcNameValidator);
  public parentValidator = inject(IhcParentValidator);
  public parentNullableValidator = inject(IhcParentNullableValidator);
  public licenseValidator = inject(IhcLicenseValidator);

  public licenseRequests: ILicenseRequest[] = [];
  public licenseRequest: ILicenseRequest = {
    id: 0,
    owner: '',
    licenseNumber: '',
    entityType: '',
    ownerChange: false,
    isSubunit: false,
    parentId: '',
    email: '',
    requestData: '',
    signature: '',
    dateSigned: '',
    approved: false,
    denied: false,
    deniedReason: '',
    finalDeterminationBy: '',
    finalDeterminationDate: ''
  }

  public requestForm = new FormGroup({
    owner: new FormControl('', Validators.required),
    licenseNumber: new FormControl('', {
      asyncValidators: [this.licenseValidator.validate.bind(this.licenseValidator)],
      updateOn: 'blur'
    }),
    ownerChange: new FormControl(false),
    parentId: new FormControl('', {
      asyncValidators: [this.parentNullableValidator.validate.bind(this.parentNullableValidator)],
      updateOn: 'blur'
    }),
    signature: new FormControl(''),
    dateSigned: new FormControl(''),
    street: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    zip: new FormControl('', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)])
  }, {
    validators: [
      this.licenseRequestSignatureValidator(),
      this.licenseRequestDateValidator()
    ]
  });

  public licenses: IInHomeCareLicense[] = [];

  public licenseForm = new FormGroup({
    classificationLevel: new FormControl('', Validators.required),
    isSubunit: new FormControl(false),
    parentAgency: new FormControl('', {
      asyncValidators: [this.parentValidator.validate.bind(this.parentValidator)],
      updateOn: 'blur'
    }),
    agencyDBAName: new FormControl('', Validators.required),
    agencyPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    agencyEmail: new FormControl('', [Validators.required, Validators.email]),
    agencyFax: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyContactName: new FormControl('', Validators.required),
    emergencyContactPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyContactEmail: new FormControl('', Validators.required),
    agencyPhysicalLocation: new FormControl('', Validators.required),
    otherLocation: new FormControl(''),
    adminDesigneeName: new FormControl('', Validators.required),
    adminDesigneeTitle: new FormControl('', Validators.required),
    qualifiedEntityName: new FormControl('', Validators.required),
    qualifiedEntityTitle: new FormControl('', Validators.required),
    registeredNurseName: new FormControl(''),
    initialLicensure: new FormControl(''),
    staffInitials: new FormControl(''),
    entityName: new FormControl('', {
      validators: [this.agencyNameValidator()],
      asyncValidators: [this.nameValidator.validate.bind(this.nameValidator)],
      updateOn: 'blur'
    }),
    entityWebsite: new FormControl('', Validators.pattern( /^(http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]/i )),
    businessInOregon: new FormControl(false),
    secretaryOfStateNumber: new FormControl(''),
    registeredBusiness: new FormControl(false),
    registeredIn: new FormControl(''),
    registryNumber: new FormControl('')
  }, {
    validators: [
      this.physicalLocationValidator()
    ]
  });

  public signatureForm = new FormGroup({
    electronicSignature: new FormControl('', Validators.required),
    signedDate: new FormControl<Date | null>(null, Validators.required),
    adminPrintTitle: new FormControl('', Validators.required)
  });

  public authService = inject(AuthService);
  public sharedService = inject(SharedService);
  public licenseService = inject(InHomeCareLicenseService);
  public changeService = inject(InHomeCareChangeService);
  public paymentService = inject(PaymentService);
  public router = inject(Router);
  public dialog = inject(MatDialog);

  ngOnInit(): void {
    this.getLicenses();
    this.setupRouteSubscription();
    this.sharedService.setUserTheme();
  }

  ngOnDestroy(): void {
    setTimeout(() => {this.routeSubscription?.unsubscribe()}, 250);
  }

  setupRouteSubscription(): void {
    this.routeSubscription = this.router.events
    .pipe(filter((event) => event instanceof NavigationStart))
    .subscribe((e) => {
      if(this.license.id > 0 && !this.locked){
        this.saveLicense();
      }
    });
  }

  getLicenses(): void {
    this.licenseService.getLicenses().subscribe({
      next: response => this.licenses = response,
      error: e => {
        console.log('error getting IHC licenses:', e);
        this.sharedService.toastr.error(e);
      },
      complete: () => {
        this.getLicenseRequests();
      }
    });
  }

  getLicenseRequests(): void {
    this.licenseService.getLicenseRequests().subscribe({
      next: response => this.licenseRequests = response,
      error: e => {
        console.log('error getting license requests', e);
        this.sharedService.toastr.error('Error getting license requests');
      },
      complete: () => {
        this.showClaimLicense = this.licenseRequests.length === 0 || this.licenseRequests.every(r => r.denied);
      }
    });
  }

  createLicenseRequest(): void {
    let form = this.requestForm.getRawValue();
    this.licenseRequest.owner = form.owner;
    this.licenseRequest.email = this.authService.email;
    this.licenseRequest.licenseNumber = form.licenseNumber;
    this.licenseRequest.ownerChange = form.ownerChange;
    this.licenseRequest.parentId = form.parentId;
    this.licenseRequest.signature = form.signature;
    this.licenseRequest.dateSigned = form.dateSigned;
    let address = {
      street: form.street,
      city: form.city,
      state: form.state,
      zip: form.zip
    };

    this.licenseRequest.requestData = JSON.stringify(address);
    this.licenseService.requestLicense(this.licenseRequest).subscribe({
      next: response => this.licenseRequest.id = response,
      complete: () => {
        this.sharedService.toastr.success('License request submitted successfully.');
        this.licenseRequests.push(this.licenseRequest);
        this.showClaimLicense = this.licenseRequests.length === 0 || this.licenseRequests.every(r => r.denied);
        this.requestForm.reset();
      },
      error: e => {
        console.log('error requesting license', e);
        this.sharedService.toastr.error('Error requesting license');
      }
    });
  }

  cancelLicenseRequest(request: ILicenseRequest): void {
    this.sharedService.openConfirm("Cancel this license request?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.cancelLicenseRequest(request.id).subscribe({
            complete: () => {
              this.sharedService.toastr.success('License request canceled successfully.');
              this.licenseRequests = this.licenseRequests.filter(r => r.id != request.id);
              this.showClaimLicense = this.licenseRequests.length === 0 || this.licenseRequests.every(r => r.denied);
            },
            error: e => {
              console.log('error canceling license request', e);
              this.sharedService.toastr.error('Error canceling license request');
            }
          });
        }
      }
    );
  }

  createLicense(): void {
    this.sharedService.openConfirm("Start An In Home Care License Application?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.createLicense().subscribe({
            next: response => this.license = response,
            error: e => {
              console.log("Error Creating Application", e);
              this.sharedService.toastr.error("Error Creating Application: " + e);
            },
            complete: () => {
              this.licenses.push(this.license);
              this.sharedService.toastr.success("Created Application!");
            }
          });
        }
      }
    );
  }

  updateLicenseForm(license: IInHomeCareLicense): void {
    this.nameValidator.licenseId = license.licenseId ?? '';
    this.licenseForm.reset({
      classificationLevel: license.classificationLevel,
      isSubunit: license.isSubunit,
      parentAgency: license.parentAgency,
      agencyDBAName: license.agencyDBAName,
      agencyPhone: license.agencyPhone,
      agencyEmail: license.agencyEmail,
      agencyFax: license.agencyFax,
      emergencyContactName: license.emergencyContactName,
      emergencyContactPhone: license.emergencyContactPhone,
      emergencyContactEmail: license.emergencyContactEmail,
      agencyPhysicalLocation: license.agencyPhysicalLocation,
      otherLocation: license.otherLocation,
      adminDesigneeName: license.adminDesigneeName,
      adminDesigneeTitle: license.adminDesigneeTitle,
      qualifiedEntityName: license.qualifiedEntityName,
      qualifiedEntityTitle: license.qualifiedEntityTitle,
      registeredNurseName: license.registeredNurseName,
      initialLicensure: license.initialLicensure,
      staffInitials: license.staffInitials,
      entityName: license.entityName,
      entityWebsite: license.entityWebsite,
      businessInOregon: license.businessInOregon,
      secretaryOfStateNumber: license.secretaryOfStateNumber,
      registeredBusiness: license.registeredBusiness,
      registeredIn: license.registeredIn,
      registryNumber: license.registryNumber
    });

    this.signatureForm.reset({
      electronicSignature: license.electronicSignature,
      adminPrintTitle: license.adminPrintTitle
    });

    if(license.signedDate){
      this.signatureForm.get('signedDate')?.setValue(new Date(license.signedDate));
    }

    if((this.locked || this.license.isRenewal) && license.id > 0){
      this.licenseForm.get('entityName')?.disable();
      this.licenseForm.get('classificationLevel')?.disable();
      this.licenseForm.get('isSubunit')?.disable();
      this.licenseForm.get('parentAgency')?.disable();
    }

    if(this.locked && license.id > 0){
      this.licenseForm.get('registeredNurseName')?.disable();
    }
  }

  updateLicense(): void {
    let form = this.licenseForm.getRawValue();
    this.license.isSubunit = form.isSubunit;
    this.license.classificationLevel = form.classificationLevel;
    this.license.parentAgency = form.parentAgency;
    this.license.agencyDBAName = form.agencyDBAName;
    this.license.agencyPhone = form.agencyPhone;
    this.license.agencyEmail = form.agencyEmail;
    this.license.agencyFax = form.agencyFax;
    this.license.emergencyContactName = form.emergencyContactName;
    this.license.emergencyContactPhone = form.emergencyContactPhone;
    this.license.emergencyContactEmail = form.emergencyContactEmail;
    this.license.agencyPhysicalLocation = form.agencyPhysicalLocation;
    this.license.otherLocation = form.otherLocation;
    this.license.adminDesigneeName = form.adminDesigneeName;
    this.license.adminDesigneeTitle = form.adminDesigneeTitle;
    this.license.qualifiedEntityName = form.qualifiedEntityName;
    this.license.qualifiedEntityTitle = form.qualifiedEntityTitle;
    this.license.registeredNurseName = form.registeredNurseName;
    this.license.initialLicensure = form.initialLicensure;
    this.license.staffInitials = form.staffInitials;
    this.license.entityName = form.entityName;
    this.license.entityWebsite = form.entityWebsite;
    this.license.businessInOregon = form.businessInOregon;
    this.license.secretaryOfStateNumber = form.secretaryOfStateNumber;
    this.license.registeredBusiness = form.registeredBusiness;
    this.license.registeredIn = form.registeredIn;
    this.license.registryNumber = form.registryNumber;
  }

  selectLicense(license: IInHomeCareLicense): void {
    this.license = license;
    this.updateLicenseForm(license);
    if(this.license.parentAgency){
      this.getParentInfo(false);
    }
  }

  closeLicense(): void {
    this.saveLicense();
    this.license = this.getEmptyLicense();
    this.updateLicenseForm(this.license);
  }

  getEmptyLicense(): IInHomeCareLicense {
    return {
      id: 0,
      userId: '',
      licenseId: '',
      enteredByStaff: false,
      assignedToId: '',
      assignedToName: '',
      finalApprovalBy: '',
      canRenew: false,
      isRenewal: false,
      electronicSignature: '',
      signedDate: '',
      applicationDate: '',
      approvedDate: '',
      deniedDate: '',
      expirationDate: '',
      entityName: '',
      entityWebsite: '',
      businessInOregon: false,
      secretaryOfStateNumber: '',
      registeredBusiness: false,
      registeredIn: '',
      registryNumber: '',
      isSubunit: false,
      parentGuid: '',
      parentAgency: '',
      parentCity: '',
      calculatedDistance: 0,
      agencyDBAName: '',
      agencyPhone: '',
      agencyEmail: '',
      agencyFax: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactEmail: '',
      agencyPhysicalLocation: '',
      otherLocation: '',
      classificationLevel: '',
      adminDesigneeName: '',
      adminDesigneeTitle: '',
      qualifiedEntityName: '',
      qualifiedEntityTitle: '',
      registeredNurseName: '',
      adminPrintName: '',
      adminPrintTitle: '',
      backgroundCheckDate: '',
      initialLicensure: '',
      staffInitials: '',
      statuses: []
    }
  }

  openPDFDialog(license: IInHomeCareLicense): void {
    this.dialog.open(ExportInHomeCareLicenseDialogComponent, {
      data: license,
      role: 'dialog',
      ariaLabel: "License PDF Dialog",
      minWidth: !this.sharedService.mobile? '800px' : '',
      maxWidth: !this.sharedService.mobile? '1000px' : '300px'
    });
  }

  viewSubunits(license: IInHomeCareLicense): void {
    if (!license.licenseId) {
      this.sharedService.toastr.error('License ID is not available');
      return;
    }
    this.changeService.getSubunits(license.licenseId).subscribe({
      next: subunits => {
        this.dialog.open(SubunitDetailsDialogComponent, {
          data: { subunits },
          role: 'dialog',
          ariaLabel: "Subunit Details Dialog",
          minWidth: !this.sharedService.mobile? '600px' : '',
          maxWidth: !this.sharedService.mobile? '1200px' : '95vw',
          maxHeight: '90vh',

        });
      },
      error: e => {
        console.log('error getting subunits', e);
        this.sharedService.toastr.error('Could not load subunit details');
      }
    });
  }

  saveLicense(): void {
    this.updateLicense();
    this.licenseService.updateLicense(this.license).subscribe({
      error: e => {
        this.sharedService.toastr.error('Error Updating Application');
        console.log('updating application error', e);
      },
      complete: () => {
        this.licenseForm.markAllAsTouched();
      }
    });
  }

  submitLicense(): void {
    this.saveLicense();
    let form = this.signatureForm.value;
    this.license.electronicSignature = form.electronicSignature || '';
    this.license.signedDate = form.signedDate?.toDateString() || '';
    this.license.adminPrintTitle = form.adminPrintTitle || '';

    this.licenseService.submitLicense(this.license).subscribe({
      error: e => {
        console.log('error', e);
        this.sharedService.toastr.error("Could Not Submit Application");
      },
      complete: () => {
        this.sharedService.toastr.success("Application Submitted");
        let licenseIndex: number = this.licenses.findIndex(l => l.id === this.license.id);
        this.licenses[licenseIndex].statuses = this.licenses[licenseIndex].statuses.filter(s => s.label !== 'Started');
        this.licenses[licenseIndex].statuses.push(
        {
          label: 'Submitted',
          archived: false,
          staffOnly: false
        });
        this.closeLicense();
      }
    });
  }

  renewLicense(licenseId: string): void {
    this.sharedService.openConfirm("Begin your renewal?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.renewLicense(licenseId).subscribe({
            next: response => {
              this.licenses.push(response)
              this.selectLicense(response);
            },
            error: e => {
              console.log('error renewing license', e);
              this.sharedService.toastr.error("Could Not Renew License");
            }
          });
        }
      }
    );
  }

  requestChange(entityId: number, entityType: string, type: string){
    let dialogData: ILicenseChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.license.id,
      licenseGuid: this.license.licenseId,
      isSubunit: this.license.isSubunit,
      entityId: entityId,
      entityType: entityType,
      originalValue: '',
      newValue: '',
      changeAction: type
    }

    if(entityType === this.sharedService.classification){
      dialogData.originalValue = this.license.classificationLevel;
    }
    else if(entityType === this.sharedService.nurse){
      dialogData.originalValue = this.license.registeredNurseName;
    }
    else if (entityType === this.sharedService.parent){
      dialogData.originalValue = this.license.parentAgency
      dialogData.newValue = this.license.entityName;
    }
    else if (entityType === this.sharedService.address){
      dialogData.originalValue = this.addresses.getPhysicalAddress(this.addresses.addresses);
    }

    this.sharedService.requestChange(dialogData);
  }

  toggleEntityNameLock(): void{
    if(this.licenseForm.get('entityName')?.disabled){
      this.licenseForm.get('entityName')?.enable();
    }
    else{
      this.licenseForm.get('entityName')?.disable();
    }
  }

  changeStep(step: StepperSelectionEvent): void {
    if(this.locked && !this.approved && !this.license.isRenewal){
      return;
    }
    if(step.previouslySelectedIndex === 1){
      this.officeHours.updateHours();
      if(!this.locked || (this.approved && this.licenseForm.valid)){
        this.saveLicense();
      }
      if(!this.approved){
        if(this.licenseForm.get('isSubunit')?.value){
          this.licenseForm.get('parentAgency')?.markAsTouched();
        }
        else{
          this.licenseForm.get('parentAgency')?.setErrors(null);
        }
        this.validateRegisteredNurse();
        this.licenseForm.get('otherLocation')?.markAsTouched();
        this.addresses.cleanup();
      }
    }

    if(this.approved && !this.license.isRenewal){
      return;
    }

    if(step.previouslySelectedIndex === 3){
      if(this.owners.managingOwner){
        this.owners.saveOwner();
      }
    }

    if(step.previouslySelectedIndex === 4){
      this.adminApplication.saveApplication();
      this.adminApplication.addresses.cleanup();
    }

    if(step.selectedIndex === 7){
      this.validationErrors = [];
      this.validateRegisteredNurse();
      this.validateSubunitDistance();
      this.validateAgencyInfo();
      this.validateBranchOperations();
      this.validateOwners();
      this.validateAdminApplication();
      this.validationErrors.push(...this.branchErrors, ...this.ownerErrors, ...this.adminErrors, ...this.documentErrors);
    }
  }

  validateSubunitDistance(): void {
    if(this.license.isSubunit){
      if(this.license.calculatedDistance > 0 && this.license.calculatedDistance < 59){
        this.distanceInvalid = true;
      }
    }
  }

  validateAgencyInfo(): void {
    this.documentErrors = [];
    // Check all form validators and add messages for each invalid validator
    Object.keys(this.licenseForm.controls).forEach(key => {
      const control = this.licenseForm.get(key);
      if (control && control.invalid && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          switch (key) {
            case 'classificationLevel':
              if (errorKey === 'required') {
                this.validationErrors.push('Classification level is required');
              }
              break;
            case 'agencyDBAName':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency DBA name is required');
              }
              break;
            case 'agencyPhone':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency phone number is required');
              } else if (errorKey === 'pattern') {
                this.validationErrors.push('Agency phone number format is invalid');
              }
              break;
            case 'agencyEmail':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency email is required');
              } else if (errorKey === 'email') {
                this.validationErrors.push('Agency email format is invalid');
              }
              break;
            case 'agencyFax':
              if (errorKey === 'pattern') {
                this.validationErrors.push('Agency fax number format is invalid');
              }
              break;
            case 'emergencyContactName':
              if (errorKey === 'required') {
                this.validationErrors.push('Emergency contact name is required');
              }
              break;
            case 'emergencyContactPhone':
              if (errorKey === 'required') {
                this.validationErrors.push('Emergency contact phone number is required');
              } else if (errorKey === 'pattern') {
                this.validationErrors.push('Emergency contact phone number format is invalid');
              }
              break;
            case 'emergencyContactEmail':
              if (errorKey === 'required') {
                this.validationErrors.push('Emergency contact email is required');
              }
              break;
            case 'agencyPhysicalLocation':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency physical location is required');
              }
              break;
            case 'adminDesigneeName':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrative designee name is required');
              }
              break;
            case 'adminDesigneeTitle':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrative designee title is required');
              }
              break;
            case 'qualifiedEntityName':
              if (errorKey === 'required') {
                this.validationErrors.push('Qualified entity name is required');
              }
              break;
            case 'qualifiedEntityTitle':
              if (errorKey === 'required') {
                this.validationErrors.push('Qualified entity title is required');
              }
              break;
            case 'entityName':
              if (errorKey === 'invalidName') {
                this.validationErrors.push('Agency legal name is already used by another approved license');
              }
              if (errorKey === 'agencyNameRequired') {
                this.validationErrors.push('Agency legal name is required and cannot contain "home health"');
              }
              break;
            case 'entityWebsite':
              if (errorKey === 'pattern') {
                this.validationErrors.push('Agency website URL format is invalid');
              }
              break;
            case 'parentAgency':
              if (errorKey === 'invalidParent') {
                this.validationErrors.push('Parent agency is invalid or not found');
              }
              break;
          }
        });
      }
    });

    // Check form-level validators
    if (this.licenseForm.errors) {
      Object.keys(this.licenseForm.errors).forEach(errorKey => {
        switch (errorKey) {
          case 'rnRequired':
            this.validationErrors.push('Registered nurse name is required for Intermediate and Comprehensive classifications');
            break;
          case 'otherLocationRequired':
            this.validationErrors.push('Other location description is required when "Other Licensed Facility or Agency Type" is selected');
            break;
        }
      });
    }

    let types: string[] = [];
    this.documents.toArray().forEach((document) => {
      let documentSet: IDocument[] = document.documents;
      documentSet.forEach((document) => {
        document.types.forEach((type) => {
          types.push(type.type);
        });
      });
    });

    if(this.licenseForm.get('agencyPhysicalLocation')?.value === 'Private Home Residence' &&
       !types.includes('Proof of Business Zoning Approval or Allowance')){
      this.documentErrors.push("Missing agency proof of business zoning approval or allowance");
    }

    if(!types.includes('IHC Policies, Procedures and Forms')){
      this.documentErrors.push('Missing IHC Policies, Procedures and Forms');
    }

    if(!types.includes('Policy and Procedure Checklist')){
      this.documentErrors.push('Missing Policy and Procedure Checklist documentation');
    }

    if(!this.hasPhysicalAddress()){
      this.validationErrors.push('Missing agency physical address');
    }

    if(this.officeHours.officeHoursForm.invalid){
      this.validationErrors.push('Missing agency office hours information');
    }
  }

  hasPhysicalAddress(): boolean {
    return this.addresses.getPhysicalAddress(this.addresses.addresses) != undefined;
  }

  addressesLoadedEvent(loaded: boolean): void {
    this.addressesLoaded = loaded;
  }

  validateBranchOperations(): void {
    this.branchErrors = [];
    this.branchErrors.push(...this.branchOperations.validateAddresses());
  }

  validateOwners(): void {
    this.ownerErrors = [];
    if(this.owners.owners.length === 0){
      this.ownerErrors.push('No owner information provided');
    }

    this.ownerErrors.push(...this.owners.validateAddresses());
    this.ownerErrors.push(...this.owners.validateDocuments());
  }

  validateAdminApplication(): void {
    this.adminErrors = [];
    // Check all application form validators and add messages for each invalid validator
    Object.keys(this.adminApplication.applicationForm.controls).forEach(key => {
      const control = this.adminApplication.applicationForm.get(key);
      if (control && control.invalid && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          switch (key) {
            case 'firstName':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator first name is required');
              }
              break;
            case 'lastName':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator last name is required');
              }
              break;
            case 'phone':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator phone number is required');
              } else if (errorKey === 'pattern') {
                this.validationErrors.push('Administrator phone number format is invalid');
              }
              break;
            case 'email':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator email is required');
              } else if (errorKey === 'email') {
                this.validationErrors.push('Administrator email format is invalid');
              }
              break;
            case 'contactWithClient':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator client contact selection is required');
              }
              break;
            case 'hasManagementExperience':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator management experience selection is required');
              }
              break;
            case 'hasProfessionalExperience':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator professional experience selection is required');
              }
              break;
          }
        });
      }
    });

    // Check administrator form-level validators
    if (this.licenseForm.errors) {
      Object.keys(this.licenseForm.errors).forEach(errorKey => {
        switch (errorKey) {
          case 'HSDateRangeInvalid':
            this.validationErrors.push('High school to date is before from date');
            break;
          case 'CollegeDateRangeInvalid':
            this.validationErrors.push('College to date is before from date');
            break;
          case 'OtherDateRangeInvalid':
            this.validationErrors.push('Other education to date is before from date');
            break;
        }
      });
    }

    if(!this.adminApplication.addresses.getPhysicalAddress(this.adminApplication.addresses.addresses)){
      this.adminErrors.push('Missing administrator physical address');
    }

    let types: string[] = [];
    this.adminApplication.documents.documents.forEach((document) => {
      document.types.forEach((type) => {
        types.push(type.type);
      });
    });

    if(!types.includes('Resume')){
      this.adminErrors.push('Missing administrator resume');
    }

    if(!types.includes('High School Diploma or Equivalent')){
      this.adminErrors.push('Missing administrator proof of high school diploma or equivalent');
    }

    if(this.adminApplication.applicationForm.get('contactWithClient')?.value && !types.includes('Background Check Request')){
      this.adminErrors.push('Missing administrator background check request');
    }

    if(!this.adminApplication.applicationForm.get('contactWithClient')?.value && !types.includes('No Client Contact Plan')){
      this.adminErrors.push('Missing administrator no client contact plan');
    }

    if(this.adminApplication.applicationForm.get('hasManagementExperience')?.value){
      if(this.adminApplication.managementExperience.experiences.length === 0){
        this.adminErrors.push('Missing administrator management experience information');
      }
    }

    if(this.adminApplication.applicationForm.get('hasProfessionalExperience')?.value){
      if(this.adminApplication.professionalExperience.experiences.length === 0){
        this.adminErrors.push('Missing administrator professional experience information');
      }
      if(!types.includes('Proof of Credential')){
        this.adminErrors.push('Missing administrator proof of credentials for professional experience');
      }
    }
  }

  getValidatedParentInfo(): void {
    setTimeout(() => {
      if(this.licenseForm.get('parentAgency')?.valid){
        this.getParentInfo(true);
      }
    }, 1000);
  }

  getParentInfo(getCity: boolean): void {
    const parentAgencyControl = this.licenseForm.get('parentAgency')?.value;
    if(!parentAgencyControl){
      this.sharedService.toastr.error('Parent agency is required');
      return;
    }

    this.licenseService.getParentInfo(parentAgencyControl).subscribe({
      next: response => {
        this.parentId = response.id;
        this.license.parentAgency = response.entityName;
        this.license.parentGuid = response.licenseId;
      },
      error: e => {
        console.log('error getting parent info', e);
        this.sharedService.toastr.error('Error getting parent info');
      },
      complete: () => {
        if(getCity){
          this.getParentCity();
        }
      }
    });
  }

  getParentCity(): void {
    this.addresses.addressService.getParentAddresses(this.parentId, this.sharedService.inHomeCareLicense).subscribe({
      next: response => {
        let parentPhysicalAddress: IAddress = response[0];
        this.license.parentCity = parentPhysicalAddress.city;
      },
      error: e => {
        console.log('error getting parent city', e);
        this.sharedService.toastr.error('Error getting parent city');
      },
      complete: () => {
        this.saveLicense();
      }
    });
  }

  getDistanceToParent(): void {
    if(!this.licenseForm.get('isSubunit')?.value){
      return;
    }

    let parentPhysicalAddress: IAddress;
    let thisPhysicalAddress: IAddress = this.addresses.getPhysicalAddress(this.addresses.addresses)!;

    if(thisPhysicalAddress === undefined){
      this.updateLicenseDistance(0);
    }

    this.addresses.addressService.getParentAddresses(this.parentId, this.sharedService.inHomeCareLicense).subscribe({
      next: response => {
        parentPhysicalAddress = response[0];
      },
      error: e => {
        console.log('error getting distance', e);
        this.sharedService.toastr.error('Error getting distance');
      },
      complete: () => {
        this.addresses.getDistanceBetweenAddressess(parentPhysicalAddress, thisPhysicalAddress);
      }
    });
  }

  updateLicenseDistance(distance: number): void {
    this.license.calculatedDistance = distance;
    if(this.license.calculatedDistance > 0 && this.license.calculatedDistance < 59){
      this.sharedService.openNotice(
        `The distance between the parent agency and the subunit appears to be fewer than 60 miles.
         You may not submit your application. Please contact the Health Care Regulation and
         Quality Improvement Section for further assistance.`)
    }
    this.saveLicense();
  }

  downloadReferenceDocument(documentType: string): void {
    this.documents.toArray()[0].getReferenceDocument(documentType);
  }

  licenseIsApproved(statuses: IStatus[]): boolean{
    return statuses.some(s => s.label === this.sharedService.approved);
  }

  public get paymentEntityType(): string {
    if(this.licenseForm.get('isSubunit')?.value){
      return 'IhcSubunit';
    }
    const classification = this.licenseForm.get('classificationLevel')?.value;
    switch(classification){
      case 'Limited':
        return 'IhcLimited';
      case 'Basic':
        return 'IhcBasic';
      case 'Intermediate':
        return 'IhcIntermediate';
      case 'Comprehensive':
        return 'IhcComprehensive';
      default:
        return '';
    }
  }

  public get locked(): boolean {
    return !this.license.statuses.some(s => s.label === this.sharedService.started);
  }

  public get approved(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.approved);
  }

  canRenew(license: IInHomeCareLicense): boolean {
    if(!license.expirationDate || license.isRenewal || this.licenses.find(l => l.licenseId === license.licenseId && l.isRenewal)){
      return false;
    }

    let expiration = new Date(license.expirationDate);
    let expirationDateWindow = new Date();
    let expirationWindow = expirationDateWindow.getDate() + 90;
    expirationDateWindow.setDate(expirationWindow);
    return expiration <= expirationDateWindow;
  }

  canSelect(license: IInHomeCareLicense): boolean {
    if(this.licenses.find(l => l.licenseId === license.licenseId && l.isRenewal && l.id !== license.id)){
      return false;
    }
    return true;
  }

  getSelectButtonLabel(license: IInHomeCareLicense): string {
    if(license.statuses.some(s => s.label === this.sharedService.started)){
      return 'Continue Application';
    }
    return 'Select';
  }

  canCancel(license: IInHomeCareLicense): boolean {
    return license.statuses.some(s => s.label === this.sharedService.started);
  }

  canViewSubunits(license: IInHomeCareLicense): boolean {
    return this.licenseIsApproved(license.statuses) && !license.isSubunit;
  }

  cancelLicense(license: IInHomeCareLicense): void {
    this.sharedService.openConfirm("Cancel this application? This action cannot be undone and will delete the application and all associated data.");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.cancelLicense(license.id).subscribe({
            complete: () => {
              this.sharedService.toastr.success('Application canceled successfully.');
              this.licenses = this.licenses.filter(l => l.id !== license.id);
            },
            error: e => {
              console.log('error canceling application', e);
              this.sharedService.toastr.error('Error canceling application');
            }
          });
        }
      }
    );
  }

  //Custom Validators
  agencyNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const entityName = control.value;
      if(!entityName){
        return { agencyNameRequired: true };
      }

      const trimmedName = entityName.replace(/\s+/g, '').toLowerCase();
      const homeHealth = 'homehealth';

      return !trimmedName.includes(homeHealth) ? null : { agencyNameRequired: true };
    }
  }

  validateAgencyName(): boolean {
    if (this.licenseForm.hasError('agencyNameRequired') && this.licenseForm.get('entityName')?.touched) {
      this.licenseForm.get('entityName')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  validateRegisteredNurse(): boolean {
    if ((this.licenseForm.get('classificationLevel')?.value === 'Comprehensive' ||
         this.licenseForm.get('classificationLevel')?.value === 'Intermediate') &&
         !this.licenseForm.get('registeredNurseName')?.value) {
      this.licenseForm.get('registeredNurseName')?.setErrors({required: true});
      return true;
    }

    return false;
  }

  physicalLocationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const agencyPhysicalLocation = control.value.agencyPhysicalLocation;
      const otherLocation = control.value.otherLocation;

      if(agencyPhysicalLocation !== 'Other Licensed Facility or Agency Type'){
        return null;
      }
      return otherLocation ? null : { otherLocationRequired: true };
    }
  }

  validatePhysicalLocation(): boolean {
    if (this.licenseForm.hasError('otherLocationRequired') && this.licenseForm.get('otherLocation')?.touched) {
      this.licenseForm.get('otherLocation')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  licenseRequestSignatureValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const ownerChange = control.value.ownerChange;
      const signature = control.value.signature;

      if(!ownerChange){
        return null;
      }
      return signature ? null : { signatureRequired: true };
    }
  }

  validateLicenseRequestSignature(): boolean {
    if (this.requestForm.hasError('signatureRequired') && this.requestForm.get('signature')?.touched) {
      this.requestForm.get('signature')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  licenseRequestDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const ownerChange = control.value.ownerChange;
      const dateSigned = control.value.dateSigned;

      if(!ownerChange){
        return null;
      }
      return dateSigned ? null : { dateRequired: true };
    }
  }

  validateLicenseRequestDate(): boolean {
    if (this.requestForm.hasError('dateRequired') && this.requestForm.get('dateSigned')?.touched) {
      this.requestForm.get('dateSigned')?.setErrors(['required']);
      return true;
    }
    return false;
  }
}
